import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';

/**
 * SEO health — Lighthouse and Core Web Vitals over time.
 *
 * Data comes from our own lambda-seo-metrics, not from Google directly: the
 * PageSpeed API key stays server-side, because this bundle is downloadable by
 * anyone even though the UI behind it is Cognito-gated.
 *
 * Mobile is shown first throughout. Google indexes mobile-first, and on this
 * site the two differ sharply — desktop has flattered the numbers all along.
 */

/** Google's own Core Web Vitals thresholds. Not our opinion. */
const THRESHOLDS = {
  lcpMs: { good: 2500, poor: 4000 },
  tbtMs: { good: 200, poor: 600 },
  cls: { good: 0.1, poor: 0.25 },
};

const GOOD = '#b8bb26';
const WARN = '#fabd2f';
const POOR = '#fb4934';
const MUTED = '#928374';

function band(metric, value) {
  const t = THRESHOLDS[metric];
  if (!t || value == null) return MUTED;
  if (value <= t.good) return GOOD;
  if (value <= t.poor) return WARN;
  return POOR;
}

function scoreColour(score) {
  if (score == null) return MUTED;
  if (score >= 90) return GOOD;
  if (score >= 50) return WARN;
  return POOR;
}

const fmtMs = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);
const fmtKb = (v) => (v == null ? '—' : `${Math.round(v / 1024)} KB`);
const fmtCls = (v) => (v == null ? '—' : v.toFixed(3));

/** Compact sparkline over the last N snapshots — no chart library needed. */
function Spark({ values, invert }) {
  const pts = values.filter((v) => v != null);
  if (pts.length < 2) return <span style={{ color: MUTED, fontSize: 11 }}>not enough history</span>;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const w = 120;
  const h = 28;
  const d = pts
    .map((v, i) => {
      const x = (i / (pts.length - 1)) * w;
      const norm = (v - min) / span;
      const y = h - (invert ? 1 - norm : norm) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const improving = invert ? pts[pts.length - 1] < pts[0] : pts[pts.length - 1] > pts[0];
  return (
    <svg width={w} height={h} role="img" aria-label="trend">
      <path d={d} fill="none" stroke={improving ? GOOD : WARN} strokeWidth="1.5" />
    </svg>
  );
}

const SeoDashboard = () => {
  const { token } = useAdminAuth();
  const [urls, setUrls] = useState([]);
  const [activeUrl, setActiveUrl] = useState('');
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const authHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token],
  );

  const loadUrls = useCallback(async () => {
    const res = await fetch(API_ENDPOINTS.ADMIN_SEO_METRICS_URLS, { headers: authHeaders });
    if (!res.ok) throw new Error(`Could not load tracked pages (HTTP ${res.status})`);
    const data = await res.json();
    setUrls(data.urls || []);
    setActiveUrl((current) => current || (data.urls || [])[0] || '');
  }, [authHeaders]);

  const loadSnapshots = useCallback(
    async (url) => {
      if (!url) return;
      const res = await fetch(`${API_ENDPOINTS.ADMIN_SEO_METRICS}?url=${encodeURIComponent(url)}&limit=30`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`Could not load history (HTTP ${res.status})`);
      const data = await res.json();
      setSnapshots(data.snapshots || []);
    },
    [authHeaders],
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError(null);
        await loadUrls();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, loadUrls]);

  useEffect(() => {
    if (!token || !activeUrl) return;
    loadSnapshots(activeUrl).catch((err) => setError(err.message));
  }, [token, activeUrl, loadSnapshots]);

  /**
   * Collection is asynchronous by necessity: API Gateway caps a request at 30
   * seconds and a full run takes several minutes, so the API returns 202 and
   * the work continues server-side. We poll for a snapshot newer than the
   * newest one we already hold, and give up politely rather than forever.
   */
  const runCollection = async () => {
    const previousLatest = snapshots[0]?.capturedAt || null;
    try {
      setCollecting(true);
      setError(null);
      setNotice(null);

      const res = await fetch(API_ENDPOINTS.ADMIN_SEO_METRICS_COLLECT, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Measurement failed (HTTP ${res.status})`);
      }
      setNotice('Measuring — this takes a few minutes. Results appear here automatically.');

      // Poll for up to ~5 minutes.
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise((r) => setTimeout(r, 10000));
        const res2 = await fetch(
          `${API_ENDPOINTS.ADMIN_SEO_METRICS}?url=${encodeURIComponent(activeUrl)}&limit=30`,
          { headers: authHeaders },
        );
        if (!res2.ok) continue;
        const data = await res2.json();
        const newest = (data.snapshots || [])[0]?.capturedAt || null;
        if (newest && newest !== previousLatest) {
          setSnapshots(data.snapshots || []);
          setNotice(null);
          return;
        }
      }
      setNotice('Still measuring. Refresh in a minute to see the result.');
    } catch (err) {
      setError(err.message);
    } finally {
      setCollecting(false);
    }
  };

  // Newest first from the API; oldest-first reads better as a trend.
  const chronological = useMemo(() => [...snapshots].reverse(), [snapshots]);
  const latest = snapshots[0];
  const mobile = latest?.mobile;
  const desktop = latest?.desktop;

  const series = (key) => chronological.map((s) => s.mobile?.[key] ?? null);

  if (!token) return <div style={{ color: MUTED, padding: 24 }}>Sign in to view SEO metrics.</div>;

  return (
    <div style={{ padding: 24, color: '#ebdbb2' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>SEO Health</h1>
        <span style={{ color: MUTED, fontSize: 13 }}>
          Lighthouse and Core Web Vitals, measured mobile-first
        </span>
        <button
          onClick={runCollection}
          disabled={collecting}
          style={{
            marginLeft: 'auto',
            background: collecting ? '#3c3836' : '#458588',
            color: '#ebdbb2',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            cursor: collecting ? 'default' : 'pointer',
            fontWeight: 600,
          }}
        >
          {collecting ? 'Measuring…' : 'Measure now'}
        </button>
      </div>
      <p style={{ color: MUTED, fontSize: 12, marginTop: 0, maxWidth: 760 }}>
        A measurement takes roughly a minute per page and calls Google once per page, per device.
        Avoid running it immediately after a deploy — a cold CDN cache reports times far worse than
        real ones.
      </p>

      {urls.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
          {urls.map((u) => (
            <button
              key={u}
              onClick={() => setActiveUrl(u)}
              style={{
                background: u === activeUrl ? '#504945' : '#3c3836',
                color: u === activeUrl ? '#ebdbb2' : '#bdae93',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {u.replace(/^https?:\/\/[^/]+/, '') || '/'}
            </button>
          ))}
        </div>
      )}

      {notice && (
        <div style={{ background: '#3c3836', border: `1px solid ${WARN}`, borderRadius: 8, padding: 12, marginBottom: 16, color: '#ebdbb2' }}>
          {notice}
        </div>
      )}

      {error && (
        <div style={{ background: '#442e2e', border: `1px solid ${POOR}`, borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <strong style={{ color: POOR }}>Could not load metrics.</strong>{' '}
          <span style={{ color: '#bdae93' }}>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ color: MUTED }}>Loading…</div>
      ) : !latest ? (
        <div style={{ background: '#3c3836', borderRadius: 8, padding: 24, color: '#bdae93' }}>
          No measurements yet. Press <strong>Measure now</strong> to take the first one — the trend
          starts from there.
        </div>
      ) : (
        <>
          <div style={{ color: MUTED, fontSize: 12, marginBottom: 12 }}>
            Last measured {new Date(latest.capturedAt).toLocaleString('en-AU')} · {snapshots.length}{' '}
            snapshot{snapshots.length === 1 ? '' : 's'} on record
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
            {[
              ['Performance', mobile?.performance, desktop?.performance],
              ['SEO', mobile?.seo, desktop?.seo],
              ['Accessibility', mobile?.accessibility, desktop?.accessibility],
              ['Best practices', mobile?.bestPractices, desktop?.bestPractices],
            ].map(([label, m, d]) => (
              <div key={label} style={{ background: '#3c3836', borderRadius: 8, padding: 16 }}>
                <div style={{ color: MUTED, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  {label}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                  <span style={{ fontSize: 32, fontWeight: 700, color: scoreColour(m) }}>{m ?? '—'}</span>
                  <span style={{ fontSize: 12, color: MUTED }}>mobile</span>
                </div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>desktop {d ?? '—'}</div>
              </div>
            ))}
          </div>

          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '28px 0 12px' }}>Core Web Vitals</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
            {[
              ['Largest Contentful Paint', 'lcpMs', fmtMs(mobile?.lcpMs), mobile?.lcpMs, false],
              ['Total Blocking Time', 'tbtMs', fmtMs(mobile?.tbtMs), mobile?.tbtMs, false],
              ['Cumulative Layout Shift', 'cls', fmtCls(mobile?.cls), mobile?.cls, false],
            ].map(([label, key, display, raw]) => (
              <div key={key} style={{ background: '#3c3836', borderRadius: 8, padding: 16 }}>
                <div style={{ color: MUTED, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  {label}
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: band(key, raw), marginTop: 6 }}>{display}</div>
                <div style={{ marginTop: 8 }}>
                  <Spark values={series(key)} invert />
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '28px 0 12px' }}>Payload</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
            {[
              ['Total page weight', fmtKb(mobile?.totalBytes), series('totalBytes')],
              ['Unused JavaScript', fmtKb(mobile?.unusedJsBytes), series('unusedJsBytes')],
              ['Time to Interactive', fmtMs(mobile?.ttiMs), series('ttiMs')],
            ].map(([label, display, vals]) => (
              <div key={label} style={{ background: '#3c3836', borderRadius: 8, padding: 16 }}>
                <div style={{ color: MUTED, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  {label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>{display}</div>
                <div style={{ marginTop: 8 }}>
                  <Spark values={vals} invert />
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '28px 0 12px' }}>History</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: MUTED, textAlign: 'left' }}>
                  {['Measured', 'Perf', 'SEO', 'LCP', 'TBT', 'CLS', 'Weight'].map((h) => (
                    <th key={h} style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #504945', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.capturedAt}>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836' }}>
                      {new Date(s.capturedAt).toLocaleString('en-AU')}
                    </td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', color: scoreColour(s.mobile?.performance), fontVariantNumeric: 'tabular-nums' }}>
                      {s.ok === false ? '—' : s.mobile?.performance ?? '—'}
                    </td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', fontVariantNumeric: 'tabular-nums' }}>{s.mobile?.seo ?? '—'}</td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', color: band('lcpMs', s.mobile?.lcpMs), fontVariantNumeric: 'tabular-nums' }}>{fmtMs(s.mobile?.lcpMs)}</td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', color: band('tbtMs', s.mobile?.tbtMs), fontVariantNumeric: 'tabular-nums' }}>{fmtMs(s.mobile?.tbtMs)}</td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', fontVariantNumeric: 'tabular-nums' }}>{fmtCls(s.mobile?.cls)}</td>
                    <td style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #3c3836', fontVariantNumeric: 'tabular-nums' }}>{fmtKb(s.mobile?.totalBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SeoDashboard;
